import type {
    BookingRequestBody,
    PatientType,
    ServiceId,
    SessionId
} from '@/types'

import { serviceOrder, sessionOrder } from '@/constants/clinic'

export const NAME_MAX = 80
export const COMPLAINT_MAX = 300

export type ValidBooking = {
    service: ServiceId
    date: string
    session: SessionId
    name: string
    phone: string
    patientType: PatientType
    complaint: string | null
}

export type ValidationResult =
    | { ok: true, value: ValidBooking }
    | { ok: false, message: string, field?: keyof BookingRequestBody }

/** Normalises Indonesian mobile numbers to 08xxxxxxxxxx, or null when invalid */
export const normalizePhone = (raw: string): string | null => {
    let digits = raw.replace(/\D/g, '')

    if (digits.startsWith('62')) digits = `0${digits.slice(2)}`
    else if (digits.startsWith('8')) digits = `0${digits}`

    return /^08\d{8,11}$/.test(digits) ? digits : null
}

export const normalizeName = (raw: string): string => {
    return raw.replace(/\s+/g, ' ').trim()
}

export const validateName = (raw: string): string | null => {
    const name = normalizeName(raw)

    if (!name) return 'Nama pasien wajib diisi.'
    if (name.length < 3) return 'Tulis nama lengkap pasien, minimal 3 huruf.'
    if (name.length > NAME_MAX) return `Nama maksimal ${NAME_MAX} karakter.`
    if (!/[a-zA-ZÀ-ɏ]/.test(name)) return 'Nama hanya boleh berisi huruf.'

    return null
}

export const validatePhone = (raw: string): string | null => {
    if (!raw.trim()) return 'Nomor WhatsApp wajib diisi.'
    if (!normalizePhone(raw)) return 'Gunakan nomor ponsel Indonesia yang aktif, misalnya 0812 3456 7890.'

    return null
}

const asString = (value: unknown): string => {
    return typeof value === 'string' ? value : ''
}

export const validateBookingInput = (body: unknown, allowedDates: string[]): ValidationResult => {
    const input = (body && typeof body === 'object' ? body : {}) as Record<string, unknown>

    const service = asString(input.service) as ServiceId
    const date = asString(input.date)
    const session = asString(input.session) as SessionId
    const patientType = asString(input.patientType) as PatientType

    if (!serviceOrder.includes(service)) return { ok: false, message: 'Pilih layanan terlebih dahulu.', field: 'service' }
    if (!allowedDates.includes(date)) return { ok: false, message: 'Tanggal tidak tersedia. Pilih salah satu tanggal yang ditampilkan.', field: 'date' }
    if (!sessionOrder.includes(session)) return { ok: false, message: 'Pilih sesi pagi atau sore.', field: 'session' }

    const nameError = validateName(asString(input.name))
    if (nameError) return { ok: false, message: nameError, field: 'name' }

    const phone = normalizePhone(asString(input.phone))
    if (!phone) return { ok: false, message: validatePhone(asString(input.phone)) ?? 'Nomor WhatsApp tidak valid.', field: 'phone' }

    if (patientType !== 'umum' && patientType !== 'jkn') return { ok: false, message: 'Pilih jenis pasien.', field: 'patientType' }

    const complaint = asString(input.complaint).trim()
    if (complaint.length > COMPLAINT_MAX) return { ok: false, message: `Keluhan maksimal ${COMPLAINT_MAX} karakter.`, field: 'complaint' }

    return {
        ok: true,
        value: {
            service,
            date,
            session,
            name: normalizeName(asString(input.name)),
            phone,
            patientType,
            complaint: complaint || null
        }
    }
}
